/**
 * Canonical keys for {@code accessControlList[].pageName} / {@code t_core_access_control.PAGE_NAME}.
 * OPS-STAB-014: persistence uses uriId (e.g. FONE:PATIENTS), not menu display labels.
 *
 * Use with {@link hasAccess} from ./checkAccess.js.
 */

/** @type {Record<string, string>} */
export const PageNames = {
  DASHBOARD: "FONE:DASHBOARD",

  // Administration
  MANAGE_ACCOUNTS: "FONE:MANAGEACCOUNTS",
  MANAGE_USERS: "FONE:MANAGEUSERROLES",
  MANAGE_ROLES: "FONE:MANAGEROLES",
  MANAGE_USER_ROLES: "FONE:MANAGEUSERROLES",
  MAIL_CONFIGURATION: "FONE:MAILCONFIG",
  AUTH_CONFIGURATION: "FONE:AUTHCONFIG",
  MAIL_SERVER_CONFIGURATION: "FONE:MAILSERVERCONFIG",
  MANAGE_ACCESS: "FONE:ACCESSCONTROL",
  MANAGE_URI: "FONE:MANAGEURI",

  // CRM
  CRM_LEADS: "FONE:CRM_LEADS",
  CRM_ACTIVITIES: "FONE:CRM_ACTIVITIES",
  CRM_FOLLOWUPS: "FONE:CRM_FOLLOWUPS",
  CRM_DASHBOARD: "FONE:CRM_DASHBOARD",

  // Workforce / EMS
  STAFF_ONBOARDING: "FONE:EMPLOYEEONBOARD",
  OFFICE_LOCATION_MAPPING: "FONE:OFFICELOCATIONMAPPINGS",
  OFFICE_LOCATIONS: "FONE:OFFICELOCATIONS",
  LEAVE_TYPE: "FONE:LEAVETYPES",
  LEAVE_APPLICATION: "FONE:LEAVEAPPLICATION",
  DEPARTMENTS: "FONE:DEPARTMENT",

  // Asset management
  ASSET_GROUP: "FONE:ASSETGROUPS",
  ASSET_SUB_GROUP: "FONE:ASSETSUBGROUPS",
  ASSET_CATEGORY: "FONE:ASSETCATEGORY",
  HARD_DISK: "FONE:HARDDISK",
  RAM: "FONE:RAM",
  PROCESSOR: "FONE:PROCESSOR",
  OPERATING_SYSTEM: "FONE:OPERATINGSYSTEM",
  ASSET_DETAILS: "FONE:MANAGEASSETS",
  SERVER_DETAILS: "FONE:MANAGESERVERS",
  MASTER_ASSETS: "FONE:MANAGEMASTERASSETS",
  /** Legacy PAGE_NAME until a dedicated uri row exists in catalog */
  VM_REQUEST: "VM Provisioning",
  /** Legacy PAGE_NAME until a dedicated uri row exists in catalog */
  ASSET_TYPE: "Asset Type",

  // ITCMS / ADMS
  IT_CHANGE_REQUEST: "FONE:ITCHANGEREQUEST",
  ADMS_CHANGE_REQUEST: "FONE:ADMSREQUESTS",

  // Healthcare portal
  PATIENT_REGISTRATION: "FONE:PATIENTS",
  DOCTOR_ONBOARDING: "FONE:DOCTORS",
  PATIENTS: "FONE:PATIENTS",
  TOKENS: "FONE:PATIENTTOKEN",
};

/**
 * Pre–uriId {@code PAGE_NAME} values still present on older roles/sessions.
 * Keys are canonical uriIds from {@link PageNames}; values are legacy labels to match.
 */
export const LEGACY_ACCESS_CONTROL_PAGE_NAMES = {
  "FONE:ACCESSCONTROL": ["Permission Matrix", "Access Control"],
  "FONE:MANAGEROLES": ["Role Management"],
  "FONE:MANAGEUSERROLES": ["User Role Assignment", "User Role"],
  "FONE:MANAGEUSERS": ["User Role Assignment", "User Role"],
  "FONE:MANAGEACCOUNTS": ["Manage Accounts", "Manage Account"],
  "FONE:MAILCONFIG": ["Mail Configuration", "Mail Config"],
  "FONE:AUTHCONFIG": ["Authentication Configuration"],
  "FONE:MAILSERVERCONFIG": ["Mail Server Configuration", "Mail Server Config"],
  "FONE:MANAGEURI": ["Manage Uri"],
  "FONE:CRM_LEADS": ["Leads", "CRM Leads"],
  "FONE:CRM_ACTIVITIES": ["Activities", "CRM Activities"],
  "FONE:CRM_FOLLOWUPS": ["Follow Ups", "CRM Follow Ups"],
  "FONE:CRM_DASHBOARD": ["Dashboard", "CRM Dashboard"],
  "FONE:EMPLOYEEONBOARD": ["Workforce Registry", "OnBoarding"],
  "FONE:DOCTORS": ["Medical Professionals"],
  "FONE:DEPARTMENT": ["Departments"],
  "FONE:PATIENTS": [
    "Patients",
    "Register / search patients",
    "Patient Registration",
    "Generate Token",
  ],
  "FONE:PATIENTTOKEN": ["Token & Queue", "Tokens"],
  "FONE:OFFICELOCATIONS": ["Office Locations"],
  "FONE:OFFICELOCATIONMAPPINGS": ["Office Location Mappings"],
  "FONE:LEAVETYPES": ["Leave Types"],
  "FONE:LEAVEAPPLICATION": ["Leave Application"],
  "FONE:ASSETGROUPS": ["Asset Group"],
  "FONE:ASSETSUBGROUPS": ["Asset Sub Group"],
  "FONE:ASSETCATEGORY": ["Asset Category"],
  "FONE:HARDDISK": ["Hard Disk"],
  "FONE:RAM": ["Ram"],
  "FONE:PROCESSOR": ["Processor"],
  "FONE:OPERATINGSYSTEM": ["Operating System"],
  "FONE:MANAGEASSETS": ["VMs & Hosts", "Asset Details"],
  "FONE:MANAGESERVERS": ["Servers"],
  "FONE:MANAGEMASTERASSETS": ["User Assets"],
  "FONE:ITCHANGEREQUEST": ["Change Request"],
  "FONE:ADMSREQUESTS": ["Asset Disposal Requests"],
  "FONE:DASHBOARD": ["Dashboard"],
};

const LEGACY_LABEL_TO_URI_ID = Object.entries(
  LEGACY_ACCESS_CONTROL_PAGE_NAMES,
).reduce((acc, [uriId, labels]) => {
  labels.forEach((label) => {
    if (label) {
      acc[label] = uriId;
    }
  });
  return acc;
}, /** @type {Record<string, string>} */ ({}));

/**
 * @param {string | undefined | null} uriId
 * @returns {string}
 */
export function extractUriSuffix(uriId) {
  if (!uriId?.trim()) {
    return "";
  }
  const colon = uriId.trim().indexOf(":");
  return colon >= 0 ? uriId.trim().slice(colon + 1) : uriId.trim();
}

/**
 * Resolve tenant/apps prefix from persisted ACL rows (first colon-delimited pageName).
 * @param {Array<{ pageName?: string }> | undefined | null} accessControlList
 * @returns {string | null}
 */
export function inferAppsCodeFromAccessList(accessControlList) {
  if (!Array.isArray(accessControlList)) {
    return null;
  }
  for (const entry of accessControlList) {
    const pageName = entry?.pageName?.trim();
    if (!pageName) {
      continue;
    }
    const colon = pageName.indexOf(":");
    if (colon > 0) {
      return pageName.slice(0, colon);
    }
  }
  return null;
}

/**
 * @param {string[]} keys
 * @param {string | null | undefined} appsCode
 * @returns {string[]}
 */
export function expandPageKeysForTenant(keys, appsCode) {
  const code = appsCode?.trim();
  if (!code || !Array.isArray(keys) || keys.length === 0) {
    return keys ?? [];
  }
  const expanded = new Set(keys);
  for (const key of keys) {
    if (!key) {
      continue;
    }
    if (key.includes(":")) {
      expanded.add(`${code}:${extractUriSuffix(key)}`);
      continue;
    }
    const uriFromLabel = LEGACY_LABEL_TO_URI_ID[key];
    if (uriFromLabel) {
      expanded.add(`${code}:${extractUriSuffix(uriFromLabel)}`);
    }
  }
  return [...expanded];
}

/**
 * All {@code pageName} values that should match a given {@link PageNames} key.
 * When {@code appsCode} is provided, also emits tenant-prefixed uriIds for the same suffix.
 * @param {string | undefined | null} pageName
 * @param {string | null | undefined} [appsCode]
 * @returns {string[]}
 */
export function resolveAccessControlPageKeys(pageName, appsCode = null) {
  if (!pageName) {
    return [];
  }
  const trimmed = pageName.trim();
  if (!trimmed) {
    return [];
  }
  if (trimmed.startsWith("FONE:")) {
    const legacy = LEGACY_ACCESS_CONTROL_PAGE_NAMES[trimmed];
    const base = legacy ? [trimmed, ...legacy] : [trimmed];
    return expandPageKeysForTenant(base, appsCode);
  }
  const colonIdx = trimmed.indexOf(":");
  if (colonIdx > 0) {
    const foneKey = `FONE:${extractUriSuffix(trimmed)}`;
    const legacy = LEGACY_ACCESS_CONTROL_PAGE_NAMES[foneKey];
    const base = legacy ? [trimmed, foneKey, ...legacy] : [trimmed, foneKey];
    return expandPageKeysForTenant(base, appsCode);
  }
  const uriId = LEGACY_LABEL_TO_URI_ID[trimmed];
  if (uriId) {
    const legacy = LEGACY_ACCESS_CONTROL_PAGE_NAMES[uriId] ?? [];
    const base = [uriId, trimmed, ...legacy.filter((l) => l !== trimmed)];
    return expandPageKeysForTenant(base, appsCode);
  }
  return expandPageKeysForTenant([trimmed], appsCode);
}
